import { compareSemver } from './semver';

export type VersionGateDecision = 'none' | 'soft' | 'force';

/**
 * Decide whether to block the app on an outdated version (#4) or suggest a
 * dismissible update (E3). Pure + testable; the hook (useAppVersionGate)
 * supplies the runtime inputs.
 *
 * deps-security — the compared version is the NATIVE BINARY's (App.getInfo),
 * not the web bundle's: the bundle travels by OTA, so a phone on an old binary
 * that already received a newer bundle must still be forced — or a native-only
 * fix (Capacitor) would never reach it.
 *
 * Rules: native-only; force when the native version is strictly below the
 * backend `mandatory_version` (force always wins); soft when below the
 * backend `latest_version`; otherwise (equal/newer, web, missing/malformed
 * data) do nothing — **fail open**, a bad backend value must never brick the app.
 */
export function decideVersionGate(params: {
  platform: string;
  /** The native binary version; unknown ⇒ fail open. */
  nativeVersion: string | null | undefined;
  mandatoryVersion: string | null | undefined;
  latestVersion?: string | null | undefined;
}): VersionGateDecision {
  const { platform, nativeVersion, mandatoryVersion, latestVersion } = params;
  if (platform === 'web' || !nativeVersion) return 'none';

  if (mandatoryVersion) {
    const cmp = compareSemver(nativeVersion, mandatoryVersion);
    if (cmp !== null && cmp < 0) return 'force';
  }

  if (latestVersion) {
    const cmp = compareSemver(nativeVersion, latestVersion);
    if (cmp !== null && cmp < 0) return 'soft';
  }

  return 'none';
}
